import { ComponentPropsWithoutRef, forwardRef } from "react";
import { LayoutUtilProps, Size } from "../../types";
import { useLayoutPropsUtil } from "../../internal/hooks";
import cx from "classnames";
import styles from "./Card.module.scss";

/**
 * Props for the Card component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 */
export type CardProps = ComponentPropsWithoutRef<"div"> &
  LayoutUtilProps & {
    /**
     * The background variant of the card.
     * @default undefined
     */
    background?: "strong" | "stronger";
    /**
     * The padding size of the card.
     * @default "medium"
     */
    padding?: Extract<Size, "0" | "xsmall" | "small" | "medium" | "large">;
  };

/**
 * Card component for displaying content in a contained, elevated container.
 *
 * Features:
 * - Optional background variants (strong, stronger)
 * - Configurable padding sizes
 * - Supports layout utilities for positioning and spacing
 *
 * @example
 * <Card padding="large" background="strong">
 *   <Text variant="headline" el="h2">Card Title</Text>
 *   <p>Card content goes here</p>
 * </Card>
 */
export const Card = forwardRef<HTMLDivElement, CardProps>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    children,
    className,
    padding = "medium",
    background,
    style,
    ...rest
  } = componentProps;

  const cardClassNames = cx(
    [styles["card"]],
    [styles[`padding-${padding}`]],
    className,
    {
      [styles[`bg-${background}`]]: !!background,
    },
  );

  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  return (
    <div
      className={cardClassNames}
      data-anv="card"
      style={styleCombined}
      ref={ref}
      {...rest}
    >
      {children}
    </div>
  );
});

Card.displayName = "Card";
